import {createAsyncThunk, createSlice, createSelector} from "@reduxjs/toolkit"
import {defaultMemoize} from "reselect"
import { toolPanelViewSlice, TOOL_PANEL_VIEW } from "./toolPanelViewSlice"
import axios from "axios"
import {DYNAMIC_PANEL_TAB_INDEX_MAP, dynamicViewSlice,} from "./dynamicViewSlice"
import {fetchNoteCitationsAndSwitchPanels, noteArticleTabsV2} from "./noteArticleTabsV2Slice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"
import {getArticleTargetKey} from "../utils/articleUtilities"
/**
 * A map of articles, where the key is either the id or workspace.articleId.
 * There should never be duplicate articles selected unless PubMed screwed up.
 * So using a map makes retrieval, sorting, and deletion easier.
 * */

export const selectAllArticles = createAsyncThunk(
	"selectedArticles/selectAll",
	async (workspaceId, {rejectWithValue, getState}) => {
		const state = getState()
		const { 
			dynamicView,
			toolPanelView,
			noteArticleTabsV2,
			notificationPanelArticles,
			recentlyReviewed,
		} = state

		if(dynamicView.byWorkspace[workspaceId] === DYNAMIC_PANEL_TAB_INDEX_MAP.TOOLS && toolPanelView.byWorkspace[workspaceId] === TOOL_PANEL_VIEW.UNASSIGNED) {
			const {data} = await axios.get(`/api/workspace/${workspaceId}/article/unassigned`)
			const articlesMap = data.map((articleWorkspace) => articleWorkspace.article)
			return {articles: articlesMap, workspaceId}
		} else if (dynamicView.byWorkspace[workspaceId] === DYNAMIC_PANEL_TAB_INDEX_MAP.TOOLS && toolPanelView.byWorkspace[workspaceId] === TOOL_PANEL_VIEW.RECENTLY_VIEWED) {
			const articles = recentlyReviewed.byWorkspace[workspaceId].articles
			const articlesMap = articles.map(articleWorkspace => articleWorkspace.article)
			return {articles: articlesMap, workspaceId}
		} else if (dynamicView.byWorkspace[workspaceId] === DYNAMIC_PANEL_TAB_INDEX_MAP.NOTE_ARTICLE_LIST) {
			const currentNoteId = noteArticleTabsV2.byWorkspace[workspaceId].currentIndex
			const articles = noteArticleTabsV2.byWorkspace[workspaceId].tabs[currentNoteId].articles
			const articlesMap = articles.map((articleWorkspace) => articleWorkspace.article)
			return {articles: articlesMap, workspaceId}
		} else if (dynamicView.byWorkspace[workspaceId] === DYNAMIC_PANEL_TAB_INDEX_MAP.NOTIFICATION) {
			const articles = notificationPanelArticles.byWorkspace[workspaceId].articles.map(articleWorkspace => articleWorkspace.article)
			return {articles, workspaceId}
		} else {
			return rejectWithValue("Select all failed, see object value for error message.")
		}
	}
)

const initialState = {
	articles: {},
	openMultiSelect: false,
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialState)
export const selectedArticlesSlice = createSlice({
	name: "selectedArticles",
	initialState: {
		byWorkspace: {}
	},
	reducers: {
		//TODO: think about refactoring or renaming this, since it does more than just "adding" a selected article
		addSelectedArticle: (state, action) => {
			const {article, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			const id = getArticleTargetKey(article)

			if (workspace.articles[id] === undefined) {
				workspace.articles[id] = article
				if(Object.keys(workspace.articles).length > 0) {
					workspace.openMultiSelect = true
				}
			} else {
				delete workspace.articles[id]
			}
		},
		addManySelectedArticles: (state, action) => {
			const {articles, workspaceId} = action.payload
			const workspace = ensureWorkspace(state, workspaceId)
			for (let article of articles) {
				const id = getArticleTargetKey(article)
				if (workspace.articles[id] === undefined) {
					workspace.articles[id] = article
				}
			}
		},
		clearSelectedArticles: (state, action) => {
			const workspace = ensureWorkspace(state, action.payload)
			workspace.articles = initialState.articles
		},
		clearSelectedAndCloseMultiSelect: (state, action) => {
			state.byWorkspace[action.payload] = initialState
		}
	},
	extraReducers: (builder) => {
		builder.addCase (
			toolPanelViewSlice.actions.setToolPanelView,
			(state, action) => {
				const { workspaceId } = action.payload
				selectedArticlesSlice.caseReducers.clearSelectedAndCloseMultiSelect(state, {payload: workspaceId})
			}
		)
		builder.addCase(
			fetchNoteCitationsAndSwitchPanels.fulfilled,
			(state, action) => {
				const { note } = action.meta.arg
				const { workspaceId } = note
				selectedArticlesSlice.caseReducers.clearSelectedAndCloseMultiSelect(state, {payload: workspaceId})
			}
		)
		builder.addCase(
			selectAllArticles.fulfilled,
			selectedArticlesSlice.caseReducers.addManySelectedArticles
		)
		builder.addCase(
			noteArticleTabsV2.actions.setCurrentTabIndex,
			(state, action) => {
				const { workspaceId } = action.payload
				selectedArticlesSlice.caseReducers.clearSelectedAndCloseMultiSelect(state, {payload: workspaceId})
			}
		)
		builder.addCase(
			dynamicViewSlice.actions.setDynamicView,
			(state, action) => {
				const { workspaceId } = action.payload
				selectedArticlesSlice.caseReducers.clearSelectedAndCloseMultiSelect(state, {payload: workspaceId})
			}
		)
	}
})

export const {
	addSelectedArticle,
	clearSelectedArticles,
	clearSelectedAndCloseMultiSelect,
} = selectedArticlesSlice.actions

export const selectSelectedArticles = (state, workspaceId) => state.selectedArticles.byWorkspace[workspaceId]?.articles ?? initialState.articles
export const selectOpenMultiSelect = (state, workspaceId) => state.selectedArticles.byWorkspace[workspaceId]?.openMultiSelect ?? initialState.openMultiSelect

export const selectSelectedArticlesAsArray = createSelector(
	[selectSelectedArticles],
	(articles) => Object.values(articles),
	// Shared selector with LRU-sized memoization to match our byWorkspace pattern:
	// maxSize: 2 covers primary + secondary workspaces reading concurrently without
	// cache thrash. Object.values() returns a new array on every call, so without
	// memoization this selector would fail Redux's inputStabilityCheck.
	{ memoize: defaultMemoize, memoizeOptions: { maxSize: 2 } }
)

export const selectIsSelectingArticles = (workspaceId) => createSelector(
	[(state) => state.selectedArticles.byWorkspace?.[workspaceId]?.articles ?? initialState.articles],
	(selectedArticles) => Object.keys(selectedArticles).length >= 1
)

export const selectNumberOfSelectedArticles = (workspaceId) => createSelector(
	[(state) => state.selectedArticles.byWorkspace?.[workspaceId]?.articles ?? initialState.articles],
	(selectedArticles) => {
		const numberOfSelected = Object.keys(selectedArticles).length
		return numberOfSelected > 0 ? numberOfSelected : "-"
	}
)

export default selectedArticlesSlice.reducer
